import type { MiddlewareAPI } from 'redux';
import 'rxjs/add/observable/of';
import 'rxjs/add/operator/delay';
import 'rxjs/add/operator/map';
import 'rxjs/add/operator/catch';
import 'rxjs/add/operator/mergeMap';
import type { ActionsObservable } from 'redux-observable';
import { Observable } from 'rxjs/Observable';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { fireTrackAnalytics } from '@atlassian/jira-analytics-web-react/src/utils/fire-track-event.tsx';
import type { AddonStatus, ErrorResponse, State } from '../../model/types';
import installChatApp from '../../rest/install-chat-app';
import {
	type Action,
	INSTALL_CHAT_APP,
	installChatAppFailure,
	loadChatSettingsPage,
	type InstallChatApp,
	type InstallChatAppFailure,
	type LoadChatSettingsPage,
} from '../../state/actions';
import { getAddonKey, getBaseUrl } from '../../state/selectors';

const sendInstallChatAppSuccessEvent = (event: UIAnalyticsEvent): void =>
	fireTrackAnalytics(event, {
		action: 'installChatAppSuccess',
	});

const sendInstallChatAppFailureEvent = (event: UIAnalyticsEvent, error: ErrorResponse): void =>
	fireTrackAnalytics(event, {
		action: 'installChatAppFailure',
		attributes: {
			error: {
				statusCode: error.statusCode,
				message: error.message,
			},
		},
	});

const handleSuccess = (): LoadChatSettingsPage => {
	const addonStatus: AddonStatus = {
		installed: true,
		enabled: true,
	};
	return loadChatSettingsPage(addonStatus);
};

const handleFailure = (errorResponse: ErrorResponse): InstallChatAppFailure =>
	installChatAppFailure(errorResponse.message);

// eslint-disable-next-line jira/import/no-anonymous-default-export
export default (action$: ActionsObservable<Action>, store: MiddlewareAPI<State>) =>
	action$.ofType(INSTALL_CHAT_APP).mergeMap((action: InstallChatApp) => {
		const state: State = store.getState();
		const baseUrl = getBaseUrl(state);
		const addonKey = getAddonKey(state);
		return (
			installChatApp(baseUrl, addonKey)
				// We delay here so the installer page doesn't flash when being displayed
				.delay(5000)
				.map(() => {
					sendInstallChatAppSuccessEvent(action.meta.analyticsEvent);
					return handleSuccess();
				})
				.catch((error: ErrorResponse) => {
					sendInstallChatAppFailureEvent(action.meta.analyticsEvent, error);
					return Observable.of(handleFailure(error));
				})
		);
	});
